import React, { useState, useEffect } from 'react';
import { TreeDeciduous, ChevronDown, Menu, X } from 'lucide-react';

const Navbar: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const bookingLink = "https://api.leadconnectorhq.com/widget/booking/C2VL0S6llGMFg5aY5whk";

  const toggleDropdown = (name: string) => {
    if (activeDropdown === name) setActiveDropdown(null);
    else setActiveDropdown(name);
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-[#0F1419]/95 backdrop-blur-md py-4 shadow-2xl border-b border-white/5' : 'bg-transparent py-6'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        {/* Logo */}
        <div 
          className="flex items-center gap-2 group cursor-pointer" 
          onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}
        >
          <div className="w-10 h-10 bg-[#1A4D2E]/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
            <TreeDeciduous className="w-7 h-7 text-[#059669]" strokeWidth={2.5} />
          </div>
          <span className="text-xl font-black tracking-tighter uppercase text-white group-hover:text-[#FF6B35] transition-colors font-sans">
            ARBOR ADVANTAGE
          </span>
        </div>

        {/* Desktop Menu */}
        <ul className="hidden lg:flex items-center gap-8 text-sm font-bold uppercase tracking-widest text-white/80">
          <li>
            <a href="https://arboradvantage.com" className="hover:text-white transition-colors">Home</a>
          </li>
          <li className="relative group">
            <button className="hover:text-white transition-colors flex items-center gap-1 uppercase tracking-widest cursor-pointer">
              Blog <ChevronDown className="w-4 h-4 transition-transform group-hover:rotate-180" />
            </button>
            <div className="absolute top-full left-0 mt-2 w-64 bg-[#1A1F26] border border-white/10 rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 py-2">
              <a href="https://arboradvantage.com/blog/category/Marketing-Automation" className="block px-6 py-3 hover:bg-white/5 hover:text-[#059669] transition-colors">Marketing Automation</a>
              <a href="https://arboradvantage.com/blog/category/Lead-Generation" className="block px-6 py-3 hover:bg-white/5 hover:text-[#059669] transition-colors">Lead Generation</a>
              <a href="https://arboradvantage.com/blog/category/Operations-and-Growth" className="block px-6 py-3 hover:bg-white/5 hover:text-[#059669] transition-colors">Operations & Growth</a>
              <a href="https://arboradvantage.com/blog/category/Reputation-Management" className="block px-6 py-3 hover:bg-white/5 hover:text-[#059669] transition-colors">Reputation Management</a>
              <a href="https://arboradvantage.com/blog/category/Tools-and-Resources" className="block px-6 py-3 hover:bg-white/5 hover:text-[#059669] transition-colors">Tools & Resources</a>
            </div>
          </li>
          <li className="relative group">
            <button className="hover:text-white transition-colors flex items-center gap-1 uppercase tracking-widest cursor-pointer">
              Resources <ChevronDown className="w-4 h-4 transition-transform group-hover:rotate-180" />
            </button>
            <div className="absolute top-full left-0 mt-2 w-64 bg-[#1A1F26] border border-white/10 rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 py-2">
              <a href="https://arboradvantage.com/post/marketing-roi-calculator" className="block px-6 py-3 hover:bg-white/5 hover:text-[#059669] transition-colors">ROI Calculator</a>
              <a href="https://arboradvantage.com/post/lead-response-speed-calculator" className="block px-6 py-3 hover:bg-white/5 hover:text-[#059669] transition-colors">Lead Response Calculator</a>
              <a href="https://arboradvantage.com/post/review-impact-calculator" className="block px-6 py-3 hover:bg-white/5 hover:text-[#059669] transition-colors">Review Impact Calculator</a>
              <a href="https://arboradvantage.com/post/storm-work-playbook" className="block px-6 py-3 hover:bg-white/5 hover:text-[#059669] transition-colors">Storm Work Playbook</a>
              <a href="https://arboradvantage.com/post/tree-service-marketing-guide" className="block px-6 py-3 hover:bg-white/5 hover:text-[#059669] transition-colors">Marketing Guide</a>
            </div>
          </li>
          <li>
            <a 
              href={bookingLink}
              className="bg-[#059669] hover:bg-[#047857] text-white px-6 py-3 rounded-lg font-black transition-all shadow-lg active:scale-95 border-2 border-transparent text-sm tracking-widest whitespace-nowrap"
            >
              FREE STRATEGY CALL →
            </a>
          </li>
        </ul>

        {/* Mobile Toggle */}
        <button 
          className="lg:hidden text-white p-2"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <X className="w-8 h-8" /> : <Menu className="w-8 h-8" />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      <div className={`fixed inset-0 bg-[#0F1419] z-[60] lg:hidden transition-transform duration-300 overflow-y-auto ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex flex-col min-h-full p-8">
          <div className="flex justify-between items-center mb-12">
            <span className="font-black text-xl uppercase tracking-tighter text-white">ARBOR ADVANTAGE</span>
            <button onClick={() => setIsMobileMenuOpen(false)}><X className="w-10 h-10 text-white" /></button>
          </div>
          <ul className="space-y-4 text-xl font-black uppercase italic tracking-tighter">
            <li><a href="https://arboradvantage.com" onClick={() => setIsMobileMenuOpen(false)} className="block py-2">Home</a></li>
            
            <li>
              <button onClick={() => toggleDropdown('blog')} className="w-full text-left py-2 flex items-center justify-between">
                Blog <ChevronDown className={`w-6 h-6 transition-transform ${activeDropdown === 'blog' ? 'rotate-180' : ''}`} />
              </button>
              {activeDropdown === 'blog' && (
                <div className="pl-4 py-2 space-y-3 text-lg font-bold not-italic normal-case tracking-normal text-gray-400">
                  <a href="https://arboradvantage.com/blog/category/Marketing-Automation" className="block">Marketing Automation</a>
                  <a href="https://arboradvantage.com/blog/category/Lead-Generation" className="block">Lead Generation</a>
                  <a href="https://arboradvantage.com/blog/category/Operations-and-Growth" className="block">Operations & Growth</a>
                  <a href="https://arboradvantage.com/blog/category/Reputation-Management" className="block">Reputation Management</a>
                  <a href="https://arboradvantage.com/blog/category/Tools-and-Resources" className="block">Tools & Resources</a>
                </div>
              )}
            </li>

            <li>
              <button onClick={() => toggleDropdown('resources')} className="w-full text-left py-2 flex items-center justify-between">
                Resources <ChevronDown className={`w-6 h-6 transition-transform ${activeDropdown === 'resources' ? 'rotate-180' : ''}`} />
              </button>
              {activeDropdown === 'resources' && (
                <div className="pl-4 py-2 space-y-3 text-lg font-bold not-italic normal-case tracking-normal text-gray-400">
                  <a href="https://arboradvantage.com/post/marketing-roi-calculator" className="block">ROI Calculator</a>
                  <a href="https://arboradvantage.com/post/lead-response-speed-calculator" className="block">Lead Response Calculator</a>
                  <a href="https://arboradvantage.com/post/review-impact-calculator" className="block">Review Impact Calculator</a>
                  <a href="https://arboradvantage.com/post/storm-work-playbook" className="block">Storm Work Playbook</a>
                  <a href="https://arboradvantage.com/post/tree-service-marketing-guide" className="block">Marketing Guide</a>
                </div>
              )}
            </li>

            <li className="pt-8 pb-12">
              <a 
                href={bookingLink}
                className="block w-full bg-[#059669] text-white text-center py-6 rounded-2xl text-xl font-black"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                GET FREE STRATEGY CALL →
              </a>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;